<script>
/**
 * pagination组件
 * 用于表格等需要切换页码的场景
 */
import sizeMixin from "../../../utils/sizeMixin";

export default {
  name: "SjPagination",
  mixins: [sizeMixin],
  props: {
    value: Number,
    validator(val) {
      return val >= 1 && Number.isInteger(val);
    },
    total: Number,
    theme: {
      type: String,
      default: "num",
      validator(v) {
        return ["border", "fill", "num"].indexOf(v) !== -1;
      }
    },
    pageSize: {
      type: Number,
      default: 10
    },
    pagerCount: {
      type: Number,
      default: 7,
      validator(val) {
        return val >= 3 && val % 2 === 1;
      }
    },
    multi: {
      type: Boolean,
      default: false
    },
    showGoto: {
      type: Boolean,
      default: true
    },
    showTotal: {
      type: Boolean,
      default: true
    },
    pageSizes: {
      type: Array,
      default() {
        return [10, 20, 50];
      }
    }
  },
  data() {
    return {
      currentPageSize: null,
      inputValue: null
    };
  },
  computed: {
    /**
     * 根据传入的total和pagesize确定页数
     */
    pageCount() {
      if (!this.total || !this.currentPageSize) return 0;
      return Math.ceil(this.total / this.currentPageSize);
    }
  },
  render() {
    const {
      value,
      pageCount,
      size,
      theme,
      pagerCount,
      multi,
      total,
      currentPageSize,
      pageSizes,
      inputValue,
      showGoto,
      showTotal
    } = this;
    /**
     * 根据pagercount计算显示的页码
     */
    let temp;
    if (pageCount > pagerCount) {
      let shownNumber = pagerCount - 2;
      if (!(value === 1 || value === pageCount)) {
        shownNumber--;
      }
      temp = [value];
      let k = 1;
      while (shownNumber > 0) {
        if (value - k > 1) {
          temp.unshift(value - k);
          shownNumber--;
        }

        if (value + k < pageCount) {
          temp.push(value + k);
          shownNumber--;
        }
        k++;
      }
      if (value !== 1) {
        if (temp[0] > 2) {
          temp.unshift("first");
        }
        temp.unshift(1);
      }
      if (value !== pageCount) {
        if (temp[temp.length - 1] < pageCount - 1) {
          temp.push("last");
        }
        temp.push(pageCount);
      }
    } else {
      temp = [...Array(pageCount)].map((d, i) => i + 1);
    }

    // todo icon
    return (
      <div
        class={["sj-pagination", `sj-pagination--${size}`, `theme-${theme}`]}
      >
        {multi
          ? [
              <span class="pageSizes">
                <span>每页</span>
                <SjSelect
                  class="select"
                  clearable={false}
                  size={size}
                  value={currentPageSize}
                  onInput={val => {
                    this.currentPageSize = val;
                  }}
                  options={pageSizes.map(d => ({
                    label: `${d}`,
                    value: d
                  }))}
                  boundariesSelector={""}
                />
                <span>条</span>
              </span>
            ]
          : null}

        {multi && showTotal ? <span class="total">共{total}条</span> : null}

        <span
          class={["prev", "item", { disabled: value <= 1 }]}
          onClick={() => {
            value > 1 && this.changeValue(value - 1);
          }}
        >
          <i class="compagi_pre icon_com" />
        </span>
        {temp.map(d => {
          const ellipsis = d === "first" || d === "last";
          return (
            <span
              class={["item", { selected: value === d }]}
              onClick={() => {
                this.changeValue(
                  ellipsis
                    ? d === "first"
                      ? value - (pagerCount - 2)
                      : value + (pagerCount - 2)
                    : d
                );
              }}
            >
              <span class={{ ellipsis }}>{ellipsis ? "..." : d}</span>
            </span>
          );
        })}
        <span
          class={["next", "item", { disabled: value >= pageCount }]}
          onClick={() => {
            value < pageCount && this.changeValue(value + 1);
          }}
        >
          <i class="compagi_pre icon_com" />
        </span>

        {multi && showGoto
          ? [
              <span class="goPage">
                <span>前往</span>
                <SjNumberInput
                  class="input"
                  size={size}
                  value={inputValue}
                  validator={val => val > 0 && val <= pageCount}
                  onInput={val => {
                    this.inputValue = val;
                  }}
                  onChange={val => {
                    this.changeValue(val);
                  }}
                />
                <span>页</span>
              </span>
            ]
          : null}
      </div>
    );
  },
  methods: {
    changeValue(value) {
      let val = Number(value);
      if (val < 1) val = 1;
      if (val > this.pageCount) val = this.pageCount;
      this.inputValue = val;
      this.$emit("input", val);
      if (val !== this.value) {
        this.$emit("change", val);
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.inputValue = value;
      }
    },

    pageSize: {
      immediate: true,
      handler(pageSize) {
        this.currentPageSize = pageSize;
      }
    },

    currentPageSize(pagesize) {
      this.$emit("sizeChange", pagesize);
      if (this.value > this.pageCount) {
        this.changeValue(this.pageCount);
      }
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
@--pagination-color: @color-available;
@--pagination-selected-color: @color-antibg;

@--pagination-theme-fill-color: @color-gray-2;
@--pagination-selected-theme-fill-color: @color-highlight;

@--pagination-disabled-color: @color-light;

@--pagination-item-border-radius: @base-border-radius;

@--pagination-item-size: 30px;
@--pagination-input-width: 60px;
@--pagination-select-width: 80px;

.sj-pagination {
  display: inline-flex;
  vertical-align: middle;
  align-items: center;
  color: @color-subinfo;

  each(@sizes, {
    &.sj-pagination--@{value} {
      @font-type: if(@value = large or @value = middle, 'body', 'caption');
      .text(@font-type);

      .item {
        @height: "@{value}-size";
        height: @@height;
        min-width: @@height;
      }
    }
  });

  .total {
    margin-left: 20px;
    margin-right: 40px;
  }

  .pageSizes {
    display: flex;
    align-items: center;
    .select {
      width: @--pagination-select-width;
      margin: 0 10px;

      /deep/ .sj-input {
        border-color: @color-gray-4!important;

        &:focus {
          border-color: @color-highlight!important;
        }
      }
    }
    margin-right: 20px;
  }

  .goPage {
    display: flex;
    align-items: center;
    margin-left: 40px;
    .input {
      width: @--pagination-input-width;
      margin: 0 6px;
    }
  }

  &.theme-fill {
    .item {
      color: @color-available;
      background: @--pagination-theme-fill-color;
      &.selected {
        border-color: @--pagination-selected-theme-fill-color;
        background-color: @--pagination-selected-theme-fill-color;
        color: @--pagination-selected-color;

        &:hover {
          color: @--pagination-selected-color;
        }
      }
      &:hover {
        border-color: @--pagination-selected-theme-fill-color;
        color: @color-available;
      }
    }

    .prev,
    .next {
      &.disabled {
        &:hover {
          border-color: transparent;
          color: @--pagination-disabled-color;
          background-color: @--pagination-theme-fill-color;
        }
      }
    }
  }

  &.theme-border {
    .item {
      color: @color-dark;
      border-color: @color-gray-4;
      &:hover {
        border-color: @--pagination-selected-theme-fill-color;
        background-color: @--pagination-selected-theme-fill-color;
        color: @--pagination-selected-color;
      }
      &.selected {
        border-color: @--pagination-selected-theme-fill-color;
        color: @color-dark;

        &:hover {
          color: @--pagination-selected-color;
        }
      }
    }

    .prev,
    .next {
      &.disabled {
        &:hover {
          border-color: @color-gray-4;
          color: @--pagination-disabled-color;
          background-color: transparent;
        }
      }
    }
  }

  .item {
    cursor: pointer;
    box-sizing: border-box;
    border: 1px solid transparent;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: @--pagination-item-border-radius;
    color: @color-available;

    &.selected {
      border-color: @--pagination-selected-theme-fill-color;
    }
    &:hover {
      color: @--pagination-selected-theme-fill-color;
    }
    & + .item {
      margin-left: 10px;
    }
    // .ellipsis {
    //   align-self: flex-end;
    //   transform: scale(0.65) rotate(90deg);
    // }
  }
  .prev,
  .next {
    i {
      font-size: 12px;
    }
    &.disabled {
      color: @--pagination-disabled-color;
      cursor: not-allowed;
    }
  }
  .next {
    transform: rotate(180deg);
  }
}
</style>
